import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const ImageCarousel = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  // ✅ Fetch dynamic slides from CodeIgniter 4 backend
  useEffect(() => {
    fetch("https://justurbane.in/backend_php/public/api/carousel")
      .then((res) => res.json())
      .then((data) => setSlides(data))
      .catch((err) => console.error("Error fetching slides:", err));
  }, []);

  // Auto advance
  useEffect(() => {
    if (slides.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides]);

  const goToSlide = (index) => setCurrentSlide(index);
  const goToPrevious = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const goToNext = () => setCurrentSlide((prev) => (prev + 1) % slides.length);

  if (slides.length === 0) {
    return (
      <section className="relative bg-white py-8 text-center">
        <p className="text-gray-400">Loading carousel...</p>
      </section>
    );
  }

  return (
    <section className="relative bg-white py-4 md:py-8">
      <div className="w-full px-2">
        {/* max-w-6xl mx-auto px-4 md:px-6 */}
        <div className="relative h-64 md:h-[300px] lg:h-[400px] overflow-hidden rounded-xl md:rounded-2xl shadow-xl md:shadow-2xl">
          
          <div className="carousel-container relative w-full h-full">
            <div 
              className="carousel-track flex transition-all duration-700 ease-out h-full"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div key={slide.id} className="carousel-slide flex-shrink-0 w-full h-full relative">
                  
                  {/* Blurred background */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center filter blur-lg scale-110 z-0"
                    style={{ backgroundImage: `url(${slide.image_url})` }}
                  ></div>

                  {/* Foreground image */}
                  <img
                    src={slide.image_url}
                    alt={slide.alt_text}
                    className="relative w-full h-full object-contain z-10 transition-transform duration-700 hover:scale-105"
                    onError={(e) => {
                      e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=600&fit=crop&crop=center&auto=format&q=90";
                    }}
                  />

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/40 to-transparent z-10"></div>

                  {/* Text & CTA */}
                  <motion.div 
                    className="absolute bottom-0 left-0 right-0 p-4 md:p-8 lg:p-12 z-20"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: currentSlide === index ? 1 : 0, y: currentSlide === index ? 0 : 30 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <div className="inline-block mb-2 md:mb-4">
                      <span className="bg-white/20 backdrop-blur-md text-white px-2 md:px-4 py-1 md:py-2 rounded-full text-xs md:text-sm font-semibold uppercase tracking-wider border border-white/30">
                        {slide.category}
                      </span>
                    </div>
                    <h2 className="text-white text-xl md:text-3xl lg:text-5xl font-serif font-bold mb-2 md:mb-4 leading-tight">
                      {slide.title}
                    </h2>
                    <p className="text-white/90 text-sm md:text-lg lg:text-xl mb-4 md:mb-6 leading-relaxed max-w-2xl">
                      {slide.description}
                    </p>
                    <button className="hidden bg-white/20 backdrop-blur-md hover:bg-white/30 text-white px-4 md:px-8 py-2 md:py-3 rounded-full font-semibold transition-all duration-300 border border-white/30 hover:border-white/50 hover:scale-105 text-sm md:text-base">
                      Explore More
                    </button>
                  </motion.div>
                </div>
              ))}
            </div>

            {/* Navigation dots */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-30">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 ${
                    currentSlide === index 
                      ? 'w-8 h-3 bg-white rounded-full' 
                      : 'w-3 h-3 bg-white/50 hover:bg-white/70 rounded-full hover:scale-110'
                  }`}
                />
              ))}
            </div>

            {/* Arrows */}
            <button 
              onClick={goToPrevious}
              className="absolute left-4 top-1/3 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white p-2 rounded-full transition-all duration-300 hover:scale-110 z-30 group"
            >
              <svg className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={goToNext}
              className="absolute right-4 top-1/3 transform -translate-y-1/2 bg-black/60 backdrop-blur-sm hover:bg-black/80 text-white p-2 rounded-full transition-all duration-300 hover:scale-110 z-30 group"
            >
              <svg className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-30">
              <div 
                className="h-full bg-white transition-all duration-300"
                style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImageCarousel;
